// src/screens/ReceiptLogScreen.js
import React, { useEffect, useState } from 'react';
import './ReceiptLogScreen.css';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import backIcon from '../assets/backspace-icon.png';

function ReceiptLogScreen() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  const limit = 15;

  const fetchReceipts = async (pageToLoad = 1) => {
    try {
      const res = await api.get(`/receipts?page=${pageToLoad}&limit=${limit}`);
      const newReceipts = Array.isArray(res.data) ? res.data : [];

      const deduped = newReceipts.filter(
        (r) => !receipts.some((existing) => existing.transactionId === r.transactionId)
      );

      setReceipts((prev) => (pageToLoad === 1 ? deduped : [...prev, ...deduped]));
      setHasMore(newReceipts.length === limit);
      setPage(pageToLoad);
    } catch (err) {
      console.error('❌ Failed to load receipts:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchReceipts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll
  useEffect(() => {
    const scrollContainer = document.getElementById('receipt-scroll');
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - 200 &&
        hasMore &&
        !loadingMore
      ) {
        setLoadingMore(true);
        fetchReceipts(page + 1);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, page]);

  const formatDate = (timestamp) => new Date(timestamp).toLocaleString();

  const usedByText = (usedBy) => {
    if (usedBy?.length === 2) return 'Used by: Buyer & Seller';
    if (usedBy?.length === 1) return `Used by: ${usedBy[0]}`;
    return null; // hide completely if unused
  };

  return (
    <div className="receipt-log-container">
      <div className="receipt-header">
        <div className="receipt-header-title-row">
          <img
            src={backIcon}
            alt="Back"
            className="receipt-back-icon"
            onClick={() => navigate(-1)}
          />
          <h2 className="receipt-log-title">Public Transaction Log</h2>
        </div>
      </div>

      <div className="receipt-list-scroll" id="receipt-scroll">
        {loading ? (
          <div className="receipt-loading">Loading receipts...</div>
        ) : receipts.length === 0 ? (
          <p className="receipt-empty">No receipts available yet.</p>
        ) : (
          <>
            <div className="receipt-list">
              {receipts.map((r) => {
                const thumb =
                  !r.productDeleted && Array.isArray(r.productMedia)
                    ? r.productMedia[0]
                    : null;

                const isClickable = !!r.productId && !r.productDeleted;

                const handleOpen = () => {
                  if (!isClickable) return;
                  // Route confirmed in App.js: /product/:id
                  navigate(`/product/${r.productId}`);
                };

                const handleKey = (e) => {
                  if (!isClickable) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen();
                  }
                };

                return (
                  <div
                    className={`receipt-card ${isClickable ? 'clickable' : 'disabled'}`}
                    key={`${r.transactionId}-${r.createdAt}`}
                    onClick={handleOpen}
                    onKeyDown={handleKey}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : -1}
                    title={isClickable ? 'View product' : 'Product no longer available'}
                  >
                    <div className="receipt-card-left">
                      <div className="receipt-id">{r.transactionId}</div>

                      <div className="receipt-user">
                        <strong>Buyer:</strong>{' '}
                        <span className="username-ellipsis" title={`@${r.buyerUsername}`}>
                          @{r.buyerUsername}
                        </span>
                      </div>

                      <div className="receipt-user">
                        <strong>Seller:</strong>{' '}
                        <span className="username-ellipsis" title={`@${r.sellerUsername}`}>
                          @{r.sellerUsername}
                        </span>
                      </div>

                      <div className="receipt-product">
                        <strong>Product:</strong>{' '}
                        {r.productDeleted ? (
                          <span className="deleted-product">Deleted Product</span>
                        ) : (
                          r.productName || 'Unnamed Product'
                        )}
                      </div>

                      {usedByText(r.usedBy) && (
                        <div className="receipt-used">{usedByText(r.usedBy)}</div>
                      )}

                      <div className="receipt-time">{formatDate(r.createdAt)}</div>
                    </div>

                    <div className="receipt-card-right">
                      {thumb ? (
                        <img src={thumb} alt="product" className="receipt-thumb" loading="lazy" />
                      ) : (
                        <div
                          className={`receipt-thumb placeholder ${r.productDeleted ? 'deleted' : ''}`}
                        >
                          <span>{r.productDeleted ? 'Deleted' : 'No image'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {loadingMore && <div className="spinner more-spinner" style={{ marginBottom: 30 }} />}
            {!hasMore && !loadingMore && receipts.length > 0 && (
              <div className="end-of-feed-msg">
                <p>You've reached the end.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating Leaderboard button */}
      <button
        className="receipt-fab"
        onClick={() => navigate('/leaderboard')}
        aria-label="Open sales leaderboard"
        title="Sales Leaderboard"
      >
        🏆 Sales Leaderboard
      </button>
    </div>
  );
}

export default ReceiptLogScreen;
